/** XeAgents.ai - Super Admin: Free Pilot Requests **/
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Rocket, CalendarCheck, CalendarX } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const BASE = "/api/admin/pilot-requests";

export interface PilotRequest {
  id: string;
  fullName: string;
  workEmail: string;
  /**
   * Null on rows the Cal.com webhook created: someone who booked a demo without
   * ever filling the form. Cal.com gives us a name and an email and nothing
   * else, so every optional column here has to render a blank rather than
   * assume a form-shaped record.
   */
  company: string | null;
  phone: string | null;
  teamSize: string | null;
  goals: string | null;
  country: string | null;
  source: "form" | "demo_booking";
  status: "new" | "contacted" | "converted" | "declined";
  /** Written only by the Cal.com webhook - never by the browser. */
  demoBookedAt: string | null;
  demoStartsAt: string | null;
  demoCancelledAt: string | null;
  createdAt: string;
}

const STATUS_OPTIONS: PilotRequest["status"][] = ["new", "contacted", "converted", "declined"];

const STATUS_BADGE_VARIANT: Record<PilotRequest["status"], "default" | "secondary" | "destructive" | "outline"> = {
  new: "default",
  contacted: "secondary",
  converted: "outline",
  declined: "destructive",
};

const BLANK = "—";

/**
 * The demo cell.
 *
 * Deliberately separate from `status`: a booking is a scheduled intention,
 * while "contacted" means a human actually spoke to them. Folding one into the
 * other would show a lead as worked when nobody has touched it.
 */
function DemoCell({ item }: { item: PilotRequest }) {
  if (item.demoCancelledAt) {
    return (
      <span className="flex items-center gap-1.5 whitespace-nowrap text-destructive">
        <CalendarX className="h-3.5 w-3.5" />
        Cancelled
      </span>
    );
  }
  if (item.demoBookedAt) {
    return (
      <span className="flex items-center gap-1.5 whitespace-nowrap text-emerald-600 dark:text-emerald-400">
        <CalendarCheck className="h-3.5 w-3.5" />
        {item.demoStartsAt ? format(new Date(item.demoStartsAt), "MMM d, HH:mm") : "Booked"}
      </span>
    );
  }
  return <span className="text-muted-foreground">{BLANK}</span>;
}

interface PilotRequestsProps {
  /** Called when a row is clicked - the caller jumps to owner onboarding pre-filled from this request. */
  onOnboard?: (id: string) => void;
}

export default function PilotRequests({ onOnboard }: PilotRequestsProps = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ items: PilotRequest[] }>({
    queryKey: [BASE],
    queryFn: async () => {
      const response = await apiRequest("GET", BASE);
      return response.json();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: PilotRequest["status"] }) => {
      const response = await apiRequest("PATCH", `${BASE}/${id}`, { status });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE] });
      toast({ title: "Status updated" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Converted requests already became an owner account - keep them out of the
  // working list rather than leaving a dead row behind once onboarding is done.
  const items = (data?.items ?? []).filter((item) => item.status !== "converted");

  // Every number here comes from a webhook-written column, so it is a
  // measurement rather than a claim the browser made on the way past.
  const bookedCount = items.filter((i) => i.demoBookedAt && !i.demoCancelledAt).length;
  const unbookedCount = items.length - bookedCount;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Rocket className="h-5 w-5" />
          Free Pilot Requests
          {items.length > 0 && (
            <Badge variant="secondary" className="ml-1 font-normal" data-testid="badge-pilot-demo-counter">
              {bookedCount}/{items.length} booked a demo
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          "Request a Free Pilot" submissions from the landing page, plus anyone who booked a demo without filling
          the form.{" "}
          {unbookedCount > 0 && (
            <span className="text-amber-600 dark:text-amber-500">
              {unbookedCount} {unbookedCount === 1 ? "lead has" : "leads have"} no demo booked.
            </span>
          )}{" "}
          {onOnboard && "Click a row to onboard it as an owner account."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-72" />
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Demo</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Team Size</TableHead>
                  <TableHead>Goals</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      No pilot requests yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow
                      key={item.id}
                      data-testid={`row-pilot-request-${item.id}`}
                      className={onOnboard ? "cursor-pointer hover:bg-muted/50" : undefined}
                      title={onOnboard ? "Click to onboard this pilot request" : undefined}
                      onClick={() => onOnboard?.(item.id)}
                    >
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          {item.fullName}
                          {item.source === "demo_booking" && (
                            <Badge
                              variant="outline"
                              className="font-normal text-xs"
                              title="Booked a demo without filling the pilot form — this record is sparse"
                            >
                              demo only
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{item.company || BLANK}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <a href={`mailto:${item.workEmail}`} className="text-primary hover:underline">
                          {item.workEmail}
                        </a>
                      </TableCell>
                      <TableCell>
                        <DemoCell item={item} />
                      </TableCell>
                      <TableCell>{item.country || BLANK}</TableCell>
                      <TableCell>{item.phone || BLANK}</TableCell>
                      <TableCell>{item.teamSize || BLANK}</TableCell>
                      <TableCell className="max-w-xs truncate" title={item.goals || undefined}>
                        {item.goals || BLANK}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(item.createdAt), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={item.status}
                          onValueChange={(status) =>
                            updateStatusMutation.mutate({ id: item.id, status: status as PilotRequest["status"] })
                          }
                        >
                          <SelectTrigger className="h-8 w-[130px]" data-testid={`select-status-${item.id}`}>
                            <SelectValue>
                              <Badge variant={STATUS_BADGE_VARIANT[item.status]} className="capitalize">
                                {item.status}
                              </Badge>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((status) => (
                              <SelectItem key={status} value={status} className="capitalize">
                                {status}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
